import React, { useState } from "react";
import { useSubscription, PlanTier } from "../../hooks/useSubscription";
import UpgradeModal from "./UpgradeModal";

interface FeatureGateProps {
  /** The feature being gated */
  feature: "automations" | "dataRoom" | "decks" | "customApiKeys";
  /** Content to render if user has access */
  children: React.ReactNode;
  /** Optional custom fallback component */
  fallback?: React.ReactNode;
  /** Whether to show a full page gate vs inline */
  fullPage?: boolean;
}

const featureConfig: Record<
  string,
  { title: string; description: string; requiredPlan: PlanTier }
> = {
  automations: {
    title: "Automations",
    description:
      "Create powerful automated workflows to streamline your investor updates and communications.",
    requiredPlan: "basic",
  },
  dataRoom: {
    title: "Data Room",
    description:
      "Securely share documents and files with investors through a professional data room.",
    requiredPlan: "pro",
  },
  decks: {
    title: "Deck Storage",
    description:
      "Upload and manage your pitch decks with analytics and secure sharing links.",
    requiredPlan: "pro",
  },
  customApiKeys: {
    title: "Custom API Keys",
    description:
      "Use your own LLM API keys for complete data control and customizable rate limits.",
    requiredPlan: "pro",
  },
};

const planNames: Record<PlanTier, string> = {
  free: "Free",
  basic: "Basic",
  pro: "Pro",
  enterprise: "Enterprise",
};

/**
 * Component to gate features based on subscription plan
 * Shows upgrade prompt if user doesn't have access
 */
export default function FeatureGate({
  feature,
  children,
  fallback,
  fullPage = true,
}: FeatureGateProps) {
  const { features, loading, canUseAutomations, canUseDataRoom, canUseDecks } =
    useSubscription();
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  // Check feature access
  const featureAccessMap: Record<FeatureGateProps["feature"], boolean> = {
    automations: canUseAutomations,
    dataRoom: canUseDataRoom,
    decks: canUseDecks,
    customApiKeys: features.canUseCustomApiKeys,
  };

  const hasAccess = featureAccessMap[feature];
  const config = featureConfig[feature];

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  // User has access - render children
  if (hasAccess) {
    return <>{children}</>;
  }

  // User doesn't have access - show fallback or default upgrade prompt
  if (fallback) {
    return <>{fallback}</>;
  }

  // Default upgrade prompt
  if (fullPage) {
    return (
      <>
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
          <div className="max-w-md text-center">
            {/* Lock Icon */}
            <div className="mb-6 flex justify-center">
              <div className="w-20 h-20 rounded-full bg-purple-900/30 flex items-center justify-center">
                <svg
                  className="w-10 h-10 text-purple-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-white mb-3">
              {config.title} is a {planNames[config.requiredPlan]} Feature
            </h2>

            {/* Description */}
            <p className="text-gray-400 mb-6">{config.description}</p>

            {/* Upgrade Button */}
            <button
              onClick={() => setIsUpgradeModalOpen(true)}
              className="su-btn-primary px-8 py-3 text-lg"
            >
              Upgrade to {planNames[config.requiredPlan].split(" ")[0]}
            </button>

            {/* Feature highlights */}
            <div className="mt-8 text-left bg-black/30 rounded-lg p-4 border border-gray-800">
              <p className="text-sm text-gray-400 mb-3">Upgrading unlocks:</p>
              <ul className="space-y-2 text-sm text-gray-300">
                {config.requiredPlan === "basic" && (
                  <>
                    <li className="flex items-center gap-2">
                      <span className="text-green-400">✓</span>
                      Unlimited manual sends
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-400">✓</span>
                      Unlimited automations
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-400">✓</span>
                      Advanced analytics
                    </li>
                  </>
                )}
                {config.requiredPlan === "pro" && (
                  <>
                    <li className="flex items-center gap-2">
                      <span className="text-green-400">✓</span>
                      Everything in Basic
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-400">✓</span>
                      Data room access
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-400">✓</span>
                      Deck storage & analytics
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-green-400">✓</span>
                      Priority support
                    </li>
                  </>
                )}
              </ul>
            </div>
          </div>
        </div>

        <UpgradeModal
          isOpen={isUpgradeModalOpen}
          onClose={() => setIsUpgradeModalOpen(false)}
        />
      </>
    );
  }

  // Inline upgrade prompt
  return (
    <>
      <div className="bg-black/30 border border-gray-800 rounded-lg p-4 text-center">
        <p className="text-gray-400 mb-3">
          {config.title} requires a {planNames[config.requiredPlan]} plan.
        </p>
        <button
          onClick={() => setIsUpgradeModalOpen(true)}
          className="su-btn-primary px-4 py-2 text-sm"
        >
          Upgrade Now
        </button>
      </div>

      <UpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
      />
    </>
  );
}

/**
 * Higher-order component version of FeatureGate
 */
export function withFeatureGate<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  feature: "automations" | "dataRoom" | "decks" | "customApiKeys"
) {
  return function WithFeatureGateComponent(props: P) {
    return (
      <FeatureGate feature={feature}>
        <WrappedComponent {...props} />
      </FeatureGate>
    );
  };
}
